import { useCallback, useMemo } from "react";
import {
  apiSpaceToVaultSpace,
  vaultSpaceToCreateInput,
  vaultSpaceToUpdateInput,
} from "../mappers";
import { useCreateSpace } from "./useCreateSpace";
import { useSpaces } from "./useSpaces";
import { useUpdateSpace } from "./useUpdateSpace";
import type { GlobalAttribute, Space } from "#common/types/vault";

export interface UseVaultSpacesOptions {
  /**
   * The presentational global-attribute catalog (the same `globalLib` passed to
   * `DocumentVault`). Used to correlate server attributes back to their vault
   * `key` when mapping server -> UI.
   */
  globalLib?: GlobalAttribute[];
  /**
   * Maps a vault global `key` -> backend `globalAttributeId`, needed to persist
   * global-attribute references on save. The backend global-catalog hook isn't
   * wired up yet; until it is, omit this and only local attributes + core fields
   * are saved (global refs are skipped — see `mappers`).
   */
  globalIdByKey?: Record<string, string>;
}

export interface UseVaultSpacesReturn {
  /** Server spaces mapped onto the presentational shape for `DocumentVault`. */
  spaces: Space[];
  loading: boolean;
  error: ReturnType<typeof useSpaces>["error"];
  refetch: ReturnType<typeof useSpaces>["refetch"];
  saving: boolean;
  /**
   * `onSaveSpace` handler for `DocumentVault` / `SpaceEditor`: creates when
   * `id` is null, otherwise updates. Resolves to the saved space (mapped back
   * to the vault shape) or `null` if the mutation returned no data.
   */
  saveSpace: (space: Space, id: string | null) => Promise<Space | null>;
}

/**
 * Container hook that bridges the Space GraphQL API (DV-195/196/197/218) to the
 * presentational `DocumentVault` / `SpaceEditor` components: it fetches spaces,
 * maps them onto the vault shape, and exposes a `saveSpace` matching the
 * components' `onSaveSpace(space, id)` callback.
 *
 * Fields with no backend representation (space contract, per-attribute
 * required/default, role scoping) are dropped on save and defaulted on read —
 * see `mappers` for the full list.
 *
 * @example
 * const { spaces, saveSpace } = useVaultSpaces({ globalLib });
 * return <DocumentVault {...rest} spaces={spaces} globalLib={globalLib} onSaveSpace={saveSpace} />;
 */
export const useVaultSpaces = (options: UseVaultSpacesOptions = {}): UseVaultSpacesReturn => {
  const { globalLib, globalIdByKey } = options;
  const { spaces: apiSpaces, loading, error, refetch } = useSpaces();
  const { createSpace, loading: creating } = useCreateSpace();
  const { updateSpace, loading: updating } = useUpdateSpace();

  const spaces = useMemo(
    () => apiSpaces.map((api) => apiSpaceToVaultSpace(api, { globalLib })),
    [apiSpaces, globalLib],
  );

  const saveSpace = useCallback(
    async (space: Space, id: string | null): Promise<Space | null> => {
      // No baseline is read or needed here any more (DV-738): the mapper sends
      // client scope declaratively from `space` as it stands now, so a stale
      // local `spaces` snapshot cannot cause a wrong diff — the backend is the
      // one place staleness gets caught, via its own guard on the asserted
      // state.
      const saved = id
        ? await updateSpace(vaultSpaceToUpdateInput(id, space, { globalIdByKey }))
        : await createSpace(vaultSpaceToCreateInput(space, { globalIdByKey }));
      return saved ? apiSpaceToVaultSpace(saved, { globalLib }) : null;
    },
    [createSpace, updateSpace, globalIdByKey, globalLib],
  );

  return {
    spaces,
    loading,
    error,
    refetch,
    saving: creating || updating,
    saveSpace,
  };
};
